"use client";

import { useMemo, useRef, type KeyboardEvent } from "react";
import {
  AlarmClock,
  Bot,
  ClipboardCheck,
  FileText,
  Folder,
  GitBranch,
  GitCompareArrows,
  Layers3,
  Lightbulb,
  ListTodo,
  Map as MapIcon,
  Palette,
  Scale,
  Settings,
  ShieldAlert,
  Sparkles,
  Target,
  Workflow,
  Waypoints,
  Compass,
  Cable,
  Signpost,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { TabElement } from "@/components/workspaces/tab-element";
import {
  DEFAULT_WORKSPACE_TABS,
  type WorkspaceTab,
  type WorkspaceTabIconId,
} from "@/components/workspaces/workspace-tabs.config";
import { cn } from "@/lib/utils";
import type { NamespaceId } from "@/types/namespaces";

const workspaceTabIcons: Record<WorkspaceTabIconId, LucideIcon> = {
  sparkles: Sparkles,
  "lightbulb": Lightbulb,
  "list-todo": ListTodo,
  "file-text": FileText,
  folder: Folder,
  "git-branch": GitBranch,
  "git-compare": GitCompareArrows,
  bot: Bot,
  workflow: Workflow,
  palette: Palette,
  settings: Settings,
  "alarm-clock": AlarmClock,
  "clipboard-check": ClipboardCheck,
  layers: Layers3,
  scale: Scale,
  "shield-alert": ShieldAlert,
  target: Target,
  map: MapIcon,
  waypoints: Waypoints,
  compass: Compass,
  cable: Cable,
  signpost: Signpost,
};

const resolveTabIcon = (icon?: WorkspaceTab["icon"]): LucideIcon | undefined => {
  if (!icon) {
    return undefined;
  }

  if (typeof icon === "string") {
    return workspaceTabIcons[icon];
  }

  return icon;
};

type WorkspaceTabsProps = {
  tabs?: ReadonlyArray<WorkspaceTab>;
  active?: NamespaceId;
  onSelectTab?: (tab: NamespaceId) => void;
  onCloseTab?: (id: NamespaceId) => void;
  closableIds?: NamespaceId[];
  className?: string;
  /**
   * Accessible name for the tablist. Override when the rail carries something
   * other than workspace tabs (e.g. the organization areas rail).
   */
  ariaLabel?: string;
  /**
   * Tabs share the rail width evenly by default (the workspace rail is a full
   * set of ~10 tabs). Set false for short rails — two stretched tabs read as a
   * split header rather than tabs.
   */
  stretchTabs?: boolean;
};

export function WorkspaceTabs({
  tabs = DEFAULT_WORKSPACE_TABS,
  active,
  onSelectTab,
  onCloseTab,
  closableIds,
  className,
  ariaLabel = "Workspace tabs",
  stretchTabs = true,
}: WorkspaceTabsProps) {
  const tabIds = useMemo(() => tabs.map((tab) => tab.id), [tabs]);
  const tabRefs = useRef<Partial<Record<NamespaceId, HTMLButtonElement | null>>>(
    {},
  );

  const activeTabId = active && tabIds.includes(active) ? active : tabIds[0];

  const focusTabByIndex = (index: number) => {
    if (tabIds.length === 0) return;
    const normalizedIndex = (index + tabIds.length) % tabIds.length;
    const nextId = tabIds[normalizedIndex];

    if (nextId) {
      onSelectTab?.(nextId);
      tabRefs.current[nextId]?.focus();
    }
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown": {
        event.preventDefault();
        focusTabByIndex(index + 1);
        break;
      }
      case "ArrowLeft":
      case "ArrowUp": {
        event.preventDefault();
        focusTabByIndex(index - 1);
        break;
      }
      case "Home": {
        event.preventDefault();
        focusTabByIndex(0);
        break;
      }
      case "End": {
        event.preventDefault();
        focusTabByIndex(tabIds.length - 1);
        break;
      }
      default:
        break;
    }
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="horizontal"
      className={cn(
        "workspace-tablist relative isolate flex w-full min-w-0 flex-wrap gap-[var(--space-xs)] items-start",
        className,
      )}
    >
      {tabs.map((tab, index) => {
        const isActive = tab.id === activeTabId;
        const isClosable = !!onCloseTab && closableIds?.includes(tab.id);
        const Icon = resolveTabIcon(tab.icon);

        return (
          <TabElement
            key={tab.id}
            wrapperClassName={cn(
              "flex min-w-0",
              // TabElement's wrapper is w-full; without a flex basis that makes
              // every tab claim the whole rail and wrap onto its own line.
              stretchTabs ? "flex-1 basis-0" : "w-auto flex-none",
            )}
            ref={(node) => {
              tabRefs.current[tab.id] = node;
            }}
            id={`workspace-tab-${tab.id}`}
            aria-controls={`workspace-panel-${tab.id}`}
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            state={isActive ? "open" : "closed"}
            label={tab.label}
            icon={Icon}
            closable={isClosable}
            closeButtonAriaLabel={`Close ${tab.label} workspace tab`}
            onClose={() => onCloseTab?.(tab.id)}
            onClick={onSelectTab ? () => onSelectTab(tab.id) : undefined}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              "min-w-0 whitespace-nowrap text-sm",
              "data-[state=open]:font-semibold data-[state=closed]:font-normal",
            )}
            data-workspace-tab-container=""
          />
        );
      })}
    </div>
  );
}

export default WorkspaceTabs;
